import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FORMAS_DE_FECHAMENTO,
  ROTULO_FORMA_DE_FECHAMENTO,
  nomeNaComanda,
  totalDaComanda,
  type FormaDeFechamento,
} from '@arena/shared/schemas';
import { dinheiro } from '../agenda/formatos.js';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { invalidarDinheiroEFatura, invalidarEstornoDeComanda } from '../api/invalidacoes.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { AvisoDeCaixa } from '../financeiro/AvisoDeCaixa.jsx';
import { avisoDeDinheiroForaDoCaixa } from '../financeiro/formatos.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Escolhas, Nota, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  avisoDoFechamento,
  contagemDeItens,
  desfechoDaComanda,
  quandoAbriu,
  textoDeComandaSemItens,
  MOTIVO_DE_CANCELAMENTO,
  motivoValido,
} from './formatos.js';

/**
 * `#m-comanda` — a comanda aberta de uma pessoa.
 *
 * O total é calculado PELO PREÇO DO ITEM, que o servidor congelou no
 * lançamento. A tela não consulta o preço do produto para somar: se
 * alguém mudar a tabela no meio da noite, a comanda aberta continua
 * cobrando o que foi combinado — e é isso que a soma precisa mostrar.
 *
 * O `totalConferido` vai junto no fechamento. É a proteção contra o
 * segundo terminal: se a comanda ganhou um item entre a conferência e o
 * clique, o servidor recusa em vez de cobrar um valor que ninguém viu.
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */
export function ModalDeComanda({
  comandaId,
  onFechar,
}: {
  comandaId: string;
  onFechar: () => void;
}) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();
  const [forma, setForma] = useState<FormaDeFechamento>('Pix');
  const [erro, setErro] = useState<string | undefined>();
  const [cancelando, setCancelando] = useState(false);
  const [estornando, setEstornando] = useState(false);

  const comanda = useQuery({
    queryKey: ['comandas', comandaId],
    queryFn: () => estoqueApi.comanda(comandaId),
  });

  const invalidar = async () => {
    await Promise.all([
      cliente.invalidateQueries({ queryKey: ['comandas'] }),
      cliente.invalidateQueries({ queryKey: ['estoque'] }),
    ]);
  };

  // Fechar mexe em muito mais que a comanda: em dinheiro, o razão e o caixa
  // (esperado); em Mensalidade, a fatura e o plano. O `staleTime` do caixa é de
  // 30 s e nada o refaz sozinho.
  const invalidarDoFechamento = async () => {
    await Promise.all([invalidar(), invalidarDinheiroEFatura(cliente)]);
  };

  const lancar = useMutation({
    mutationFn: (produtoId: string) =>
      estoqueApi.lancarItem(comandaId, { produtoId, quantidade: 1 }),
    onSuccess: invalidar,
    onError: (e: unknown) => setErro(mensagemDoErro(e)),
  });

  const ajustar = useMutation({
    mutationFn: ({ itemId, quantidade }: { itemId: string; quantidade: number }) =>
      estoqueApi.ajustarItem(comandaId, itemId, quantidade),
    onSuccess: invalidar,
    onError: (e: unknown) => setErro(mensagemDoErro(e)),
  });

  const fechar = useMutation({
    mutationFn: (total: number) =>
      estoqueApi.fecharComanda(comandaId, { forma, totalConferido: total }),
    onSuccess: async (resultado) => {
      await invalidarDoFechamento();
      avisar(avisoDoFechamento(resultado));
      if (resultado.divergencias.length > 0) {
        avisar(
          `Atenção: ${resultado.divergencias.map((d) => d.nome).join(', ')} ficou com saldo ` +
            'negativo. Faça o inventário.',
          'aviso',
        );
      }
      onFechar();
    },
    onError: (e: unknown) => setErro(mensagemDoErro(e)),
  });

  // O painel do Bar já diz se há caixa aberto; é a mesma chave da página, então
  // costuma vir do cache. Sem resposta (ainda, ou falhou), não se avisa.
  const painel = useQuery({
    queryKey: ['comandas', 'painel'],
    queryFn: () => estoqueApi.painel(7),
    enabled: pode('comanda', 'fechar'),
    staleTime: 30_000,
  });
  const caixaAberto = painel.data ? painel.data.caixaAberto !== null : undefined;
  const aceitaMensalidade = Boolean(comanda.data?.aceitaMensalidade);

  // O servidor pode deixar de aceitar a mensalidade depois de um refetch (o
  // plano do aluno acabou): a forma escolhida some das opções e não pode
  // ficar selecionada, ou o fechamento sairia 409.
  useEffect(() => {
    if (!aceitaMensalidade && forma === 'Mensalidade') setForma('Pix');
  }, [aceitaMensalidade, forma]);

  const dados = comanda.data;
  const total = dados ? totalDaComanda(dados.itens) : 0;
  // Fechada OU cancelada: a comanda que o histórico abre. Vira só leitura —
  // sem busca, sem mais e menos, sem fechamento. O servidor recusaria tudo
  // isso com 409; a tela não oferece o que já sabe que vai ser recusado.
  const encerrada = Boolean(dados?.fechadaEm || dados?.canceladaEm);
  const podeLancar = pode('comanda', 'lancar') && !encerrada;
  const podeFechar = pode('comanda', 'fechar') && !encerrada;
  const desfecho = dados ? desfechoDaComanda(dados) : null;
  // Estornar é da comanda FECHADA (a cancelada não tem venda a desfazer), e a
  // matriz decide quem: só o Dono por padrão. Cosmético — o servidor confere.
  const fechadaSemCancelar = Boolean(dados?.fechadaEm && !dados.canceladaEm);
  const podeEstornar = pode('comanda', 'estornar') && fechadaSemCancelar;

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={dados ? `Comanda · ${nomeNaComanda(dados)}` : 'Comanda'}
      descricao={
        dados
          ? `${quandoAbriu(dados)}${dados.observacao ? ` · ${dados.observacao}` : ''}`
          : undefined
      }
      rodape={
        encerrada ? (
          <>
            <span className="left">{desfecho}</span>
            {podeEstornar && !estornando && (
              <Botao variante="contorno" onClick={() => setEstornando(true)}>
                Estornar fechamento
              </Botao>
            )}
            <Botao variante="fantasma" onClick={onFechar}>
              Fechar
            </Botao>
          </>
        ) : (
          <>
            <span className="left">Uma comanda aberta por pessoa</span>
            {!cancelando && pode('comanda', 'cancelar') && dados?.itens.length === 0 && (
              <Botao variante="fantasma" onClick={() => setCancelando(true)}>
                Cancelar comanda
              </Botao>
            )}
            <Botao variante="fantasma" onClick={onFechar}>
              Manter aberta
            </Botao>
            <Botao
              onClick={() => fechar.mutate(total)}
              disabled={!podeFechar || fechar.isPending || !dados}
            >
              {fechar.isPending ? 'Fechando…' : 'Fechar comanda'}
            </Botao>
          </>
        )
      }
    >
      <CorpoDeModal>
        {comanda.isError && <Nota>{mensagemDoErro(comanda.error)}</Nota>}
        {erro && <Nota>{erro}</Nota>}

        {podeLancar && <BuscaDeProduto onEscolher={(produto) => lancar.mutate(produto.id)} />}

        <div>
          <div className="card-head" style={{ marginBottom: 10 }}>
            <h2>Itens</h2>
            <span className="hint">{dados ? contagemDeItens(dados) : '—'}</span>
          </div>

          {dados && dados.itens.length === 0 ? (
            <Vazio>{textoDeComandaSemItens(dados)}</Vazio>
          ) : (
            <ul style={{ display: 'flex', flexDirection: 'column' }}>
              {dados?.itens.map((item) => (
                <li
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 0',
                    borderTop: '1px solid var(--line-soft)',
                  }}
                >
                  <span style={{ flex: 1, fontSize: 13 }}>
                    <b>{item.produto.nome}</b>
                    <small style={{ display: 'block', color: 'var(--dim)' }}>
                      {dinheiro(item.precoUnitario)} cada
                    </small>
                  </span>
                  {podeLancar && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Botao
                        pequeno
                        variante="contorno"
                        aria-label={`Tirar uma unidade de ${item.produto.nome}`}
                        onClick={() =>
                          ajustar.mutate({ itemId: item.id, quantidade: item.quantidade - 1 })
                        }
                      >
                        −
                      </Botao>
                      <b className="num">{item.quantidade}</b>
                      <Botao
                        pequeno
                        variante="contorno"
                        aria-label={`Somar uma unidade de ${item.produto.nome}`}
                        onClick={() =>
                          ajustar.mutate({ itemId: item.id, quantidade: item.quantidade + 1 })
                        }
                      >
                        +
                      </Botao>
                    </span>
                  )}
                  <b className="num" style={{ minWidth: 76, textAlign: 'right' }}>
                    {dinheiro(item.quantidade * item.precoUnitario)}
                  </b>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 14,
            background: 'var(--bg)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-sm)',
          }}
        >
          <span className="sub">
            {fechadaSemCancelar && dados?.formaPagamento
              ? `Total fechado · ${ROTULO_FORMA_DE_FECHAMENTO[dados.formaPagamento]}`
              : 'Total da comanda'}
          </span>
          <b className="num" style={{ fontSize: 20, fontWeight: 800 }}>
            {dinheiro(total)}
          </b>
        </div>

        {podeFechar && (
          <Campo rotulo="Fechamento">
            <Escolhas
              aria-label="Forma de fechamento"
              valor={forma}
              onChange={setForma}
              opcoes={FORMAS_DE_FECHAMENTO.filter(
                // "Lançar na mensalidade" só quando o servidor diz que a comanda
                // aceita: é de um aluno E algum plano dele ainda vai gerar
                // fatura. O avulso, e o aluno cadastrado sem plano, fecham
                // como qualquer venda — oferecer o botão seria oferecer um
                // 400 ou um 409.
                (opcao) => opcao !== 'Mensalidade' || Boolean(dados?.aceitaMensalidade),
              ).map((opcao) => ({ valor: opcao, rotulo: ROTULO_FORMA_DE_FECHAMENTO[opcao] }))}
            />
          </Campo>
        )}

        {podeFechar && avisoDeDinheiroForaDoCaixa(forma, caixaAberto) && <AvisoDeCaixa />}

        {cancelando && (
          <CancelamentoDaComanda
            comandaId={comandaId}
            invalidar={invalidar}
            onVoltar={() => setCancelando(false)}
            onFeito={onFechar}
          />
        )}

        {podeEstornar && estornando && (
          <EstornoDoFechamento
            comandaId={comandaId}
            invalidar={invalidar}
            onVoltar={() => setEstornando(false)}
            onFeito={() => setEstornando(false)}
          />
        )}

        {!encerrada && (
          <Nota>
            Ao fechar, o sistema baixa o estoque dos itens consumidos e lança a venda como entrada
            no financeiro, no centro de custo <b>Bar</b>. Em "lançar na mensalidade" o valor vai
            para a próxima fatura do aluno — a que já está em aberto, ou, se ainda não nasceu, fica
            pendente e entra nela. O dinheiro entra quando a fatura for paga.
          </Nota>
        )}
      </CorpoDeModal>
    </Modal>
  );
}

/**
 * "Estornar fechamento": devolve o estoque, desfaz o dinheiro (o lançamento no
 * razão, ou o consumo na fatura) e REABRE a comanda, para corrigir e fechar de
 * novo. Pede o motivo, no mesmo painel de `CorrecoesDaFatura`.
 *
 * Depois de estornar a comanda continua na tela, agora ABERTA: quem corrige
 * costuma querer mexer nos itens em seguida. O refetch a reabre; o que dependia
 * dela (estoque, a aba Comandas do aluno e as faturas) é invalidado.
 */
function EstornoDoFechamento({
  comandaId,
  invalidar,
  onVoltar,
  onFeito,
}: {
  comandaId: string;
  invalidar: () => Promise<unknown>;
  onVoltar: () => void;
  onFeito: () => void;
}) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | undefined>();

  const estornar = useMutation({
    mutationFn: () => estoqueApi.estornarComanda(comandaId, motivo.trim()),
    onSuccess: async () => {
      await Promise.all([
        invalidar(),
        // Na mensalidade o valor desce da fatura (e a ficha do aluno muda); em
        // dinheiro, o razão e o caixa mudam.
        invalidarEstornoDeComanda(cliente),
      ]);
    },
  });

  async function confirmar() {
    if (!motivoValido(motivo) || estornar.isPending) return;
    setErro(undefined);
    try {
      await estornar.mutateAsync();
      avisar('Fechamento estornado. A comanda está aberta de novo, com o estoque devolvido.');
      onFeito();
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        background: 'var(--bg)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-sm)',
      }}
    >
      <Campo
        rotulo="Por que estornar o fechamento?"
        ajuda={`De ${MOTIVO_DE_CANCELAMENTO.minimo} a ${MOTIVO_DE_CANCELAMENTO.maximo} caracteres. O estoque volta, o dinheiro é desfeito e a comanda reabre.`}
        erro={erro}
      >
        <input
          value={motivo}
          maxLength={MOTIVO_DE_CANCELAMENTO.maximo}
          onChange={(e) => setMotivo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void confirmar();
            }
          }}
          aria-label="Motivo do estorno"
          autoFocus
        />
      </Campo>
      <div style={{ display: 'flex', gap: 8 }}>
        <Botao variante="fantasma" pequeno onClick={onVoltar}>
          Voltar
        </Botao>
        <Botao
          pequeno
          onClick={() => void confirmar()}
          disabled={!motivoValido(motivo) || estornar.isPending}
        >
          {estornar.isPending ? 'Estornando…' : 'Estornar fechamento'}
        </Botao>
      </div>
    </div>
  );
}

/**
 * "Digite parte do nome: coca, agua, acai..." — a busca com sugestão.
 *
 * Usa a busca da COMANDA (`comanda:lancar`), e não a do estoque: quem
 * lança item é quem precisa achar o produto, inclusive o Professor, que
 * não tem `estoque`. A resposta traz só nome, preço e saldo — nunca custo.
 * Quem a monta confere `comanda:lancar` (cosmético: o servidor decide).
 */
function BuscaDeProduto({ onEscolher }: { onEscolher: (produto: { id: string }) => void }) {
  const [termo, setTermo] = useState('');

  const sugestoes = useQuery({
    queryKey: ['comandas', 'busca-de-produto', termo],
    queryFn: () => estoqueApi.buscarProdutoDaComanda(termo),
    enabled: termo.trim().length > 0,
    staleTime: 10_000,
    retry: (tentativas, erro) => !(erro instanceof ErroDeApi) && tentativas < 2,
  });

  return (
    <Campo rotulo="Lançar item" ajuda="Busca por trecho do nome, sem acento.">
      <div style={{ position: 'relative' }}>
        <input
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          placeholder="Digite parte do nome: coca, agua, acai..."
          autoComplete="off"
        />
        {sugestoes.data && sugestoes.data.length > 0 && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 46,
              zIndex: 5,
              background: 'var(--panel-2)',
              border: '1px solid var(--line)',
              borderRadius: 10,
              overflow: 'hidden',
              boxShadow: 'var(--shadow)',
            }}
          >
            {sugestoes.data.map((produto) => (
              <button
                key={produto.id}
                type="button"
                className="sugestao"
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 12px',
                  background: 'transparent',
                  border: 0,
                  color: 'var(--txt)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onClick={() => {
                  onEscolher(produto);
                  setTermo('');
                }}
              >
                <span>{produto.nome}</span>
                <span className="num" style={{ color: 'var(--dim)' }}>
                  {dinheiro(produto.preco)} · {produto.saldo} un
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Campo>
  );
}

/**
 * "Cancelar comanda": pede o motivo antes de enviar. O cancelamento fica na
 * trilha, e um texto fixo ("aberta por engano") não diz nada a quem for
 * conferir depois. O mesmo painel de `CorrecoesDaFatura`; o servidor exige
 * de 3 a 200 caracteres.
 */
function CancelamentoDaComanda({
  comandaId,
  invalidar,
  onVoltar,
  onFeito,
}: {
  comandaId: string;
  invalidar: () => Promise<unknown>;
  onVoltar: () => void;
  onFeito: () => void;
}) {
  const avisar = useToast();
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | undefined>();

  const cancelar = useMutation({
    mutationFn: () => estoqueApi.cancelarComanda(comandaId, motivo.trim()),
    onSuccess: invalidar,
  });

  async function confirmar() {
    if (!motivoValido(motivo) || cancelar.isPending) return;
    setErro(undefined);
    try {
      await cancelar.mutateAsync();
      avisar('Comanda cancelada.');
      onFeito();
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        background: 'var(--bg)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-sm)',
      }}
    >
      <Campo
        rotulo="Por que cancelar a comanda?"
        ajuda={`De ${MOTIVO_DE_CANCELAMENTO.minimo} a ${MOTIVO_DE_CANCELAMENTO.maximo} caracteres.`}
        erro={erro}
      >
        <input
          value={motivo}
          maxLength={MOTIVO_DE_CANCELAMENTO.maximo}
          onChange={(e) => setMotivo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void confirmar();
            }
          }}
          aria-label="Motivo do cancelamento"
          autoFocus
        />
      </Campo>
      <div style={{ display: 'flex', gap: 8 }}>
        <Botao variante="fantasma" pequeno onClick={onVoltar}>
          Voltar
        </Botao>
        <Botao
          pequeno
          onClick={() => void confirmar()}
          disabled={!motivoValido(motivo) || cancelar.isPending}
        >
          {cancelar.isPending ? 'Cancelando…' : 'Cancelar comanda'}
        </Botao>
      </div>
    </div>
  );
}
